import { startTransition, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';

import { subscribe, subscribeToAll, unsubscribeFromAll } from '../utils/notifications';

import { maybeCurrentUserState } from '../store';

import type { NotificationPayload, User } from '@rewardopl/types';

type EventPayload = {
  notification: {
    data: NotificationPayload;
  };
  source: 'push-handler';
  type: 'notification-click';
};

export default function PushHandler() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useAtom(maybeCurrentUserState);

  const setCurrentUserWithTransition = useCallback(
    (nextUser: User) => {
      startTransition(() => setCurrentUser(nextUser));
    },
    [setCurrentUser],
  );

  // Automatically subscribe user to all notifications
  useEffect(() => {
    if (!currentUser || currentUser.notifications) {
      return;
    }

    subscribeToAll().then(setCurrentUserWithTransition);
  }, [currentUser, setCurrentUserWithTransition]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    const hasSubscribedToAnyCategory =
      currentUser.notifications && Object.values(currentUser.notifications).some(Boolean);

    if (hasSubscribedToAnyCategory) {
      subscribe(currentUser)
        .then(setCurrentUserWithTransition)
        .catch(() => {
          // Intentionally empty
        });
    } else {
      unsubscribeFromAll(currentUser)
        .then(setCurrentUserWithTransition)
        .catch(() => {
          // Intentionally empty
        });
    }
  }, [currentUser, setCurrentUserWithTransition]);

  useEffect(() => {
    if (!navigator.serviceWorker) {
      return;
    }

    function handleNotificationClick(data: EventPayload) {
      const { notification } = data;

      switch (notification.data.category) {
        case 'challenges': {
          const { challenge_id } = notification.data;
          const url = `/challenges/${challenge_id}`;
          navigate(url);
          break;
        }
        case 'messages': {
          const { message_id } = notification.data;
          const url = `/inbox/${message_id}`;
          navigate(url);
          break;
        }
        case 'products': {
          const { product_id } = notification.data;
          const url = `/products/${product_id}`;
          navigate(url);
          break;
        }
        case 'general':
        default: {
          const url = '/';
          navigate(url);
          break;
        }
      }
    }

    function onMessage(event: MessageEvent<EventPayload>) {
      const { data } = event;

      if (data.source === 'push-handler') {
        switch (data.type) {
          case 'notification-click':
            handleNotificationClick(data);
            break;
          default:
            throw new Error(`Unknown push handler message type: ${data.type satisfies never}`);
        }
      }
    }

    navigator.serviceWorker.addEventListener('message', onMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
    };
  }, [navigate]);

  return null;
}
